// Customer credit ("carnet de crédit") account and ledger types.
// Mirrors backend/src/credit/entities, service result interfaces and dto field-for-field.

export enum CreditAccountStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  CLOSED = 'CLOSED',
}

export enum CreditTransactionType {
  CHARGE = 'CHARGE',
  PAYMENT = 'PAYMENT',
  ADJUSTMENT = 'ADJUSTMENT',
}

export interface CreditAccount {
  id: string;
  organizationId: string;
  customerId: string;
  // Credit reads join an explicit column list, never the full User relation (User carries an
  // unstripped `password` column), so only these five customer fields are ever serialized.
  customer?: {
    id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  } | null;
  // numeric(10,2) columns, but the backend's DecimalTransformer converts them before serialization,
  // so they arrive as JSON numbers rather than the strings pg returns for decimals.
  creditLimit: number;
  balance: number;
  status: CreditAccountStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreditTransaction {
  id: string;
  creditAccountId: string;
  organizationId: string;
  type: CreditTransactionType;
  // Always stored positive except for manual signed adjustments; the type carries the ledger sign
  // (CHARGE adds, PAYMENT and ADJUSTMENT subtract).
  amount: number;
  orderId?: string | null;
  recordedByUserId: string;
  note?: string | null;
  // Append-only ledger: rows are never mutated, so there is no updatedAt.
  createdAt: string;
}

export interface CreditAccountSummary extends CreditAccount {
  transactions: CreditTransaction[];
}

export interface CreditStatement {
  account: CreditAccount;
  from: string;
  to: string;
  openingBalance: number;
  transactions: CreditTransaction[];
  closingBalance: number;
}

export interface CreditReconciliation {
  accountId: string;
  storedBalance: number;
  ledgerBalance: number;
  reconciled: boolean;
  difference: number;
}

export interface SetCreditLimitPayload {
  creditLimit: number;
}

export interface RecordPaymentPayload {
  amount: number;
  note?: string;
}

export interface RecordAdjustmentPayload {
  // Signed: positive writes debt off, negative puts debt back on the account.
  amount: number;
  // Mandatory here, unlike on a payment: a statement reader must be able to tell a correction apart.
  note: string;
}

export interface QueryCreditTransactionsParams {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
}

// Matches backend's PagePaginationResult<T> envelope (createPaginatedResponse), the same shape the
// cash-drawer list endpoints use.
export interface PaginatedCreditAccountsResponse {
  data: CreditAccount[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}
